import React, { useState, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Bold, Italic, List, ListChecks, Code, Link2, Image as ImageIcon,
  Columns2, Eye, PencilLine, Loader2,
} from 'lucide-react';
import { api } from '../api/client';
import { MicButton } from './UI';

// Gemeinsamer Markdown-Editor: links tippen, rechts live gerendert.
// Bilder koennen per Strg+V (Screenshot aus der Zwischenablage), Drag&Drop
// oder Button eingefuegt werden — sie landen als Attachment im Backend.

const MODES = [
  { id: 'split', icon: Columns2, title: 'Geteilt (Text + gerendert)' },
  { id: 'edit', icon: PencilLine, title: 'Nur Text' },
  { id: 'preview', icon: Eye, title: 'Nur gerendert' },
];

export function MarkdownPreview({ value, empty = 'Nichts notiert.' }) {
  if (!value) {
    return <span style={{ color: 'var(--text-3)', fontStyle: 'italic', fontSize: 13 }}>{empty}</span>;
  }
  return (
    <div className="markdown-body">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ node, ...props }) => <a {...props} target="_blank" rel="noreferrer" />,
          img: ({ node, ...props }) => (
            <a href={props.src} target="_blank" rel="noreferrer">
              <img {...props} alt={props.alt || ''} loading="lazy" />
            </a>
          ),
        }}
      >
        {value}
      </ReactMarkdown>
    </div>
  );
}

export default function MarkdownEditor({
  value = '',
  onChange,
  placeholder = 'Markdown: **fett**, *kursiv*, - Listen, [Links](url), `code`, Tabellen. Screenshot einfach mit Strg+V einfügen.',
  minHeight = 180,
  mic = false,
  defaultMode = 'split',
  storageKey = 'md-editor-mode',
  toolbar = true,
}) {
  const [mode, setMode] = useState(() => {
    try { return localStorage.getItem(storageKey) || defaultMode; } catch { return defaultMode; }
  });
  const [uploading, setUploading] = useState(0);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  // onChange ist ein einfacher Setter (kein State-Updater), darum halten wir den
  // aktuellen Text in einer Ref — sonst ueberschreiben parallele Uploads einander.
  const valueRef = useRef(value);
  valueRef.current = value;

  const commit = useCallback((next) => {
    valueRef.current = next;
    onChange(next);
  }, [onChange]);

  const pickMode = (m) => {
    setMode(m);
    try { localStorage.setItem(storageKey, m); } catch { /* Private Mode o.ae. */ }
  };

  // Text an der Cursorposition ersetzen und den Cursor sinnvoll weitersetzen.
  const replaceSelection = useCallback((makeText) => {
    const base = valueRef.current;
    const el = textareaRef.current;
    const start = el ? el.selectionStart : base.length;
    const end = el ? el.selectionEnd : base.length;
    const inserted = makeText(base.slice(start, end));
    commit(base.slice(0, start) + inserted + base.slice(end));
    requestAnimationFrame(() => {
      if (!textareaRef.current) return;
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(start, start + inserted.length);
    });
  }, [commit]);

  const wrap = (before, after = before, fallback = 'Text') =>
    replaceSelection(sel => `${before}${sel || fallback}${after}`);

  const prefixLines = (prefix) =>
    replaceSelection(sel => (sel || 'Punkt').split('\n').map(l => `${prefix}${l}`).join('\n'));

  // ── Bild-Upload ──────────────────────────────────────
  const uploadFiles = useCallback(async (files) => {
    const images = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (!images.length) return;
    setError(null);

    for (const file of images) {
      const token = `![\u23F3 ${(file.name || 'Screenshot').replace(/[[\]]/g, '')} wird hochgeladen \u2026]()`;
      // Platzhalter sofort einfuegen, damit sichtbar ist wo das Bild landet.
      const base = valueRef.current;
      const el = textareaRef.current;
      const at = el && document.activeElement === el ? el.selectionStart : base.length;
      const lead = at > 0 && base[at - 1] !== '\n' ? '\n' : '';
      commit(base.slice(0, at) + lead + token + '\n' + base.slice(at));
      setUploading(n => n + 1);

      try {
        const { url } = await api.uploadAttachment(file);
        const label = (file.name || 'Screenshot').replace(/[[\]]/g, '');
        commit(valueRef.current.replace(token, `![${label}](${url})`));
      } catch (err) {
        setError(err.message);
        commit(valueRef.current.replace(token + '\n', '').replace(token, ''));
      } finally {
        setUploading(n => n - 1);
      }
    }
  }, [commit]);

  const handlePaste = (e) => {
    const files = Array.from(e.clipboardData?.files || []).filter(f => f.type.startsWith('image/'));
    if (!files.length) return;
    e.preventDefault();
    uploadFiles(files);
  };

  const handleDrop = (e) => {
    setDragOver(false);
    const files = Array.from(e.dataTransfer?.files || []).filter(f => f.type.startsWith('image/'));
    if (!files.length) return;
    e.preventDefault();
    e.stopPropagation();
    uploadFiles(files);
  };

  const handleKeyDown = (e) => {
    if (!(e.metaKey || e.ctrlKey)) return;
    const k = e.key.toLowerCase();
    if (k === 'b') { e.preventDefault(); wrap('**'); }
    else if (k === 'i') { e.preventDefault(); wrap('*'); }
    else if (k === 'k') { e.preventDefault(); replaceSelection(sel => `[${sel || 'Text'}](url)`); }
  };

  const showEditor = mode !== 'preview';
  const showPreview = mode !== 'edit';

  const toolBtn = {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 26, height: 24, border: 'none', borderRadius: 4, cursor: 'pointer',
    background: 'transparent', color: 'var(--text-2)',
  };

  return (
    <div>
      {toolbar && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
          marginBottom: 6, padding: 2, background: 'var(--bg-2)',
          border: '1px solid var(--border)', borderRadius: 'var(--radius)',
        }}>
          <button type="button" style={toolBtn} title="Fett (Strg+B)" onClick={() => wrap('**')}><Bold size={13} /></button>
          <button type="button" style={toolBtn} title="Kursiv (Strg+I)" onClick={() => wrap('*')}><Italic size={13} /></button>
          <button type="button" style={toolBtn} title="Code" onClick={() => wrap('`', '`', 'code')}><Code size={13} /></button>
          <button type="button" style={toolBtn} title="Link (Strg+K)" onClick={() => replaceSelection(sel => `[${sel || 'Text'}](url)`)}><Link2 size={13} /></button>
          <div style={{ width: 1, height: 16, background: 'var(--border)', margin: '0 4px' }} />
          <button type="button" style={toolBtn} title="Liste" onClick={() => prefixLines('- ')}><List size={13} /></button>
          <button type="button" style={toolBtn} title="Checkliste" onClick={() => prefixLines('- [ ] ')}><ListChecks size={13} /></button>
          <div style={{ width: 1, height: 16, background: 'var(--border)', margin: '0 4px' }} />
          <button type="button" style={toolBtn} title="Bild hochladen (oder Screenshot mit Strg+V einfügen)"
            onClick={() => fileInputRef.current?.click()}>
            {uploading > 0
              ? <Loader2 size={13} className="spin" />
              : <ImageIcon size={13} />}
          </button>
          {mic && <MicButton onTranscript={(text) => commit(valueRef.current ? `${valueRef.current} ${text}` : text)} />}

          <div style={{ flex: 1 }} />
          <div style={{ display: 'flex', gap: 2 }}>
            {MODES.map(({ id, icon: Icon, title }) => (
              <button key={id} type="button" title={title} onClick={() => pickMode(id)}
                style={{
                  ...toolBtn,
                  background: mode === id ? 'var(--bg-3)' : 'transparent',
                  color: mode === id ? 'var(--text)' : 'var(--text-3)',
                }}>
                <Icon size={13} />
              </button>
            ))}
          </div>
        </div>
      )}

      <input ref={fileInputRef} type="file" accept="image/*" multiple hidden
        onChange={e => { uploadFiles(e.target.files); e.target.value = ''; }} />

      <div
        onDragOver={e => { if (e.dataTransfer?.types?.includes('Files')) { e.preventDefault(); setDragOver(true); } }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        style={{
          display: 'grid',
          gridTemplateColumns: showEditor && showPreview ? '1fr 1fr' : '1fr',
          gap: 8,
          outline: dragOver ? '2px dashed var(--accent)' : 'none',
          outlineOffset: 2, borderRadius: 'var(--radius)',
        }}>
        {showEditor && (
          <textarea
            ref={textareaRef}
            value={value}
            onChange={e => commit(e.target.value)}
            onPaste={handlePaste}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            spellCheck
            style={{
              minHeight, resize: 'vertical', width: '100%',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: 12.5, lineHeight: 1.6,
            }}
          />
        )}
        {showPreview && (
          <div style={{
            minHeight, overflow: 'auto',
            background: 'var(--bg-2)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius)', padding: '10px 14px',
          }}>
            <MarkdownPreview value={value} empty="Noch nichts geschrieben." />
          </div>
        )}
      </div>

      {(dragOver || error) && (
        <div style={{ fontSize: 11, marginTop: 4, color: error ? 'var(--red)' : 'var(--accent)' }}>
          {error || 'Bild hier fallen lassen …'}
        </div>
      )}
    </div>
  );
}
